import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client';

/* Plan presets (MC-611) — plans are data, edited on the Plans page. */

export interface PlanProvision {
  max_instances: number | null;
  max_size: string | null;
}

export interface PlanEntitlement {
  limits: { max_users: number | null; max_plugins: number | null };
  plugins: unknown;
  smtp: unknown;
  ssl: string;
  features: Record<string, boolean>;
}

export interface PlanRecord {
  slug: string;
  name: string;
  price_usd: number | null;
  provision: PlanProvision;
  entitlement: PlanEntitlement;
  tenant_count: number;
}

export type PlanBody = Omit<PlanRecord, 'tenant_count'>;

const key = ['plans'] as const;

export function usePlans() {
  return useQuery({
    queryKey: key,
    queryFn: () => apiFetch<PlanRecord[]>('/plans'),
    staleTime: 15_000,
  });
}

function useInvalidatePlans() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: key });
    // Plan edits change tenant Plan cards + Costs revenue.
    qc.invalidateQueries({ queryKey: ['tenants'] });
    qc.invalidateQueries({ queryKey: ['costs'] });
  };
}

export function useCreatePlan() {
  const invalidate = useInvalidatePlans();
  return useMutation({
    mutationFn: (body: PlanBody) =>
      apiFetch<PlanRecord>('/plans', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: invalidate,
  });
}

export function usePatchPlan() {
  const invalidate = useInvalidatePlans();
  return useMutation({
    mutationFn: ({ slug, ...body }: { slug: string } & Partial<PlanBody>) =>
      apiFetch<PlanRecord>(`/plans/${slug}`, { method: 'PATCH', body: JSON.stringify(body) }),
    onSuccess: invalidate,
  });
}

export function useDeletePlan() {
  const invalidate = useInvalidatePlans();
  return useMutation({
    mutationFn: (slug: string) => apiFetch<void>(`/plans/${slug}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}